import React from 'react';
import _ from 'lodash';

import {
  Box,
  Grid,
  Link,
  styled
} from '@mui/material';
import {
  fields,
  LoadingOverlay,
  Table
} from 'iron-forge-react-modules';
import {
  OverallOrderStatusChip,
  routeDirectory
} from 'modules';

import * as dataService from './dataService';
import * as types from './types';



const FormContainerBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(2)
}));

const TableComponent = Table.genTableComponent<types.IColumnId>();



export interface IProps {
  storeId: string;
}

export const FC: React.FC<IProps> = props => {
  const {
    pageInfo,
    queryTuple,
    fnQueryPage,
    fnQuery
  } = dataService.useQuery(props.storeId);
  const [freeText, setFreeText] = React.useState('');
  const [after, setAfter] = React.useState<Date | null>(null);
  const [before, setBefore] = React.useState<Date | null>(null);



  const processSubmit = React.useCallback(
    (after: Date | null, before: Date | null, freeText: string) => {
      fnQuery({
        filter: {
          freeText,
          after,
          before
        }
      });
    },
    // This is disabled here because the fnWrappedQuery inside of `useListQuery` appears to generate a new reference
    // each render. This causes the `useCallback` hook to render again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const debouncedProcessSubmit = React.useMemo(
    () => _.debounce(processSubmit, 1000),
    [processSubmit]
  );

  React.useEffect(
    () => {
      if (freeText || before || after) {
        debouncedProcessSubmit(after, before, freeText);
      }
    },
    [
      after,
      before,
      freeText,
      debouncedProcessSubmit
    ]
  );



  const rows = React.useMemo(
    () => _.map(queryTuple.data.orders, (order): types.IRow => ({
      id: order.id,
      columnId2Value: {
        orderDate: {
          type: 'string',
          label: order.orderDate.toFormat('yyyy-MM-dd @ hh:mm a')
        },
        orderStatus: {
          type: 'component',
          sortableValue: order.orderStatusName,
          label: (
            <OverallOrderStatusChip.FC
              statusName={order.orderStatusName}
              label={order.orderStatusDisplayName} />
          )
        },
        orderNumber: {
          type: 'component',
          sortableValue: order.orderNumber,
          label: (
            <Link href={routeDirectory.partner.storeDetail.path(props.storeId ?? '', order.id)}>
              {order.orderNumber}
            </Link>
          )
        },
        customerFullName: {
          type: 'string',
          label: order.customerFullName
        },
        customerEmail: {
          type: 'string',
          label: order.customerEmail
        },
        orderTotal: {
          type: 'string',
          label: '$' + order.orderTotalDollars.toFixed(2)
        },
        requestedShippingService: {
          type: 'string',
          label: order.requestedShippingService
        }
      }
    })),
    [
      props.storeId,
      queryTuple.data.orders
    ]
  );

  return (
    <>
      <LoadingOverlay.FC active={queryTuple.loading} />

      <FormContainerBox>
        <Grid
          container
          spacing={1}>
          <Grid
            item
            xs={8}>
            <fields.TextField.FC
              label="Search"
              isDisabled={queryTuple.loading}
              value={freeText}
              onChange={setFreeText} />
          </Grid>

          <Grid
            item
            xs={2}>
            <fields.DateField.FC
              isDisabled={queryTuple.loading}
              label="After"
              value={after}
              onChange={setAfter} />
          </Grid>

          <Grid
            item
            xs={2}>
            <fields.DateField.FC
              isDisabled={queryTuple.loading}
              label="Before"
              value={before}
              onChange={setBefore} />
          </Grid>
        </Grid>
      </FormContainerBox>

      <TableComponent
        disableSorting
        paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
        rows={rows}
        initSortOptions={{
          columnId: 'orderDate',
          direction: 'descending'
        }}
        columns={[
          {
            id: 'orderNumber',
            label: "Order Number"
          },
          {
            id: 'orderStatus',
            label: "Order Status"
          },
          {
            id: 'customerEmail',
            label: "Email"
          },
          {
            id: 'orderDate',
            label: "Order Date"
          },
          {
            id: 'customerFullName',
            label: "Customer Name"
          },
          {
            id: 'orderTotal',
            label: "Total"
          },
          {
            id: 'requestedShippingService',
            label: "Requested Shipping Service"
          }
        ]}
      />
    </>
  );
};
